import React, { useState } from 'react';
import { postMessageToRuntime } from '../../../Components/Helpers/Runtime/runtime';
import { FailurePublishContent } from '../../../Components/PublishToRuntime/FailurePublish';
import UpgardeContent from '../../../Components/Shared/Subscription/UpgradeContent';
import { AliceGPTCreateProjectForm } from '../../../Components/Dashboard/Project/AliceGPT/AliceGPTDialog';
import { createProject, createProjectUsingAI, getProjectPublishStatus } from '../../../Components/Dashboard/Project/AliceGPT/utils';
import useAliceGPT from '../../../Hooks/AliceGPT/useAliceGPT';
import UpdateDisplayPages from '../../../Components/Dashboard/Project/AliceGPT/UpdateDisplayPages';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from 'react-bootstrap';
import ShuffleMessage from '../../../Components/Shared/ShuffleMessage/ShuffleMessage';
import { messagesWhenCreatingProject } from '../../../Components/Shared/ShuffleMessage/MessagesHelper';
import { useTranslation } from 'react-i18next';

function WorkspaceCreateProjectPage({ organization, backToAppLibrary, showBackButton }) {

    const { t } = useTranslation("workspace_create_project_page", { useSuspense: false });

    const [state, dispatch] = useAliceGPT();

    const [projectLimitReached, setProjectLimitReached] = useState(false);
    const [showConfirmCreateProjectUsingAIDialog, setShowConfirmCreateProjectUsingAIDialog] = useState(false);
    const [projectRequestBody, setProjectRequestBody] = useState(null);
    const [isCreatingProject, setIsCreatingProject] = useState(false);

    const [publishError, setPublishError] = useState(null);
    const [showUpdateDisplay, setShowUpdateDisplay] = useState(false);
    const [createdProject, setCreatedProject] = useState(null);

    if (projectLimitReached) {
        return (
            <div style={{ width: "80%" }} className="d-flex justify-content-center align-items-center m-auto h-75">
                <UpgardeContent
                    organization={organization}
                    Headline={'Need to build <br/>more apps? '}
                    Type={"Pro"}
                    isShowFromWorkspace={true}
                />
            </div>
        )
    }

    if (publishError) {
        return (
            <div className='h-100'>
                <FailurePublishContent
                    title="Something went wrong while publishing your app"
                    error={publishError}
                    isShowFromWorkspace={false}
                    onClose={() => setPublishError(null)}
                />
            </div>
        )
    }

    function toggleUpgradeDialog() {
        setProjectLimitReached(true);
    }

    function toggleCreateProjectUsingAIDialog() {
        setShowConfirmCreateProjectUsingAIDialog(!showConfirmCreateProjectUsingAIDialog);
    }

    function toggleIsCreatingProject() {
        setIsCreatingProject(!isCreatingProject);
    }

    function handleCreateClick(projectRequestBody) {
        toggleCreateProjectUsingAIDialog();
        setProjectRequestBody(projectRequestBody);
    }

    async function handleCreate() {
        try {
            toggleIsCreatingProject();
            const response = await createProject(projectRequestBody);
            if (response.status === "Failure") {
                if (response.message === "Max apps allowed limit reached") {
                    toggleUpgradeDialog();
                } else {
                    throw new Error(response.message);
                }
            }
            setCreatedProject(response.data);
            postMessageToRuntime({
                type: "projectCreated",
                openAppUrl: response.data.appUrl
            })
        } catch (error) {
            setPublishError(error.message);
            console.error(error);
        } finally {
            toggleIsCreatingProject();
        }
    }

    async function handleCreateUsingAI() {
        try {
            toggleIsCreatingProject();
            const projectDetails = await createProjectUsingAI(projectRequestBody);
            let response = {}
            while (response.status !== "PUBLISH_SUCCESS" && response.status !== "PUBLISH_FAILED") {
                response = await getProjectPublishStatus(projectDetails.publishStatusId);
                debugger;
                if (response.status !== "PUBLISH_SUCCESS" && response.status !== "PUBLISH_FAILED") {
                    await new Promise(resolve => setTimeout(resolve, 30000));
                }
            }
            if (response.status === "PUBLISH_SUCCESS") {
                response = response.publishResponse;
            }
            if (response.status === "PUBLISH_FAILED") {
                if (response.message === "Max apps allowed limit reached") {
                    toggleUpgradeDialog();
                } else {
                    throw new Error(response.message);
                }
            }
            setCreatedProject(response.data);
            postMessageToRuntime({
                type: "projectCreated",
                openAppUrl: response.data.appUrl
            })
        } catch (error) {
            setPublishError(error.message);
            console.error(error);
        } finally {
            toggleIsCreatingProject();
        }
    }

    async function skipAIAndCreateProject(projectRequestBody) {
        try {
            toggleIsCreatingProject();
            const response = await createProject(projectRequestBody);
            if (response.status === "Failure") {
                if (response.message === "Max apps allowed limit reached") {
                    toggleUpgradeDialog();
                } else {
                    throw new Error(response.message);
                }
            }
            setCreatedProject(response.data);
            postMessageToRuntime({
                type: "projectCreated",
                openAppUrl: response.data.appUrl
            })
        } catch (error) {
            setPublishError(error.message);
            console.error(error);
        } finally {
            toggleIsCreatingProject();
        }
    }

    if (showUpdateDisplay && createdProject) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <UpdateDisplayPages
                    organizationId={organization._id}
                    projectId={createdProject._id}
                    instance={createdProject.instance}
                    url={createdProject.appUrl}
                />
            </div>
        )
    }

    if (isCreatingProject) {
        return (
            <div className="d-flex flex-column justify-content-enter align-items-center w-100 h-100">
                <div style={{ height: "80%" }}>
                    <img
                        alt="loader-clone"
                        height={"100%"}
                        src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/loaders/library.gif`} />
                </div>
                <ShuffleMessage
                    data={messagesWhenCreatingProject}
                    interval={3000} />
            </div>
        )
    }

    return (
        <>
            {
                showConfirmCreateProjectUsingAIDialog &&
                <Dialog title="" width={"28%"}>
                    <div className='d-flex flex-column text-center'>
                        <div className='w-100'>
                            <div className='position-absolute right-0'>
                                <button className='btn bg-white border-0' onClick={toggleCreateProjectUsingAIDialog}>
                                    <i className="k-button-icon k-icon k-i-x" />
                                </button>
                            </div>
                            <img
                                height={150}
                                src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/create_app_options_dialog.png`}
                            />
                        </div>
                        <div className='w-100 mt-3'>
                            <h4>{t("generation_method_dialog.heading")}</h4>
                            {/* <div style={{ fontWeight: "400" }}>Choose how to create your app</div> */}
                            {/* <div style={{ fontWeight: "400" }}>Either a quick setup with our system or a more accurate option with AI assistance.</div> */}
                            <h5 style={{ fontWeight: "400" }}>
                                {t("generation_method_dialog.description")}
                            </h5>
                        </div>
                    </div>
                    <div className='d-flex w-100 justify-content-center align-items-center my-4'>
                        <Button onClick={handleCreate} variant='secondary'>{t("generation_method_dialog.secondary_method")}</Button>
                        &nbsp;&nbsp;&nbsp;
                        <Button onClick={handleCreateUsingAI}>{t("generation_method_dialog.primary_method")}</Button>
                    </div>
                </Dialog>
            }
            <AliceGPTCreateProjectForm
                organizationId={organization._id}
                organizationName={organization.orgName}
                defaultLanguage={organization.platformLanguage}
                showAIFields={organization.isAIEnabled}
                showLanguageDropdown={organization.languageSelectionOnAppCreation}
                toggleUpgradeDialog={toggleUpgradeDialog}
                backToAppLibrary={backToAppLibrary}
                showBackButton={showBackButton ? showBackButton : false}
                onCreate={handleCreateClick}
                skipAIAndCreateProject={skipAIAndCreateProject} />
        </>
    )

}

WorkspaceCreateProjectPage.defaultProps = {
    organization: {},
    user: {}
}

export default WorkspaceCreateProjectPage;